import { useEffect, useState } from "react";
import {
  fetchPolicies,
  updatePolicy,
} from "../modules/wallet/services/walletService";
import "../css/WalletPolicy.css";
import Layout from "./Layout";

const UNIT_MAP = {
  minChargeAmount: "원",
  maxChargeAmount: "원",
  chargeUnit: "원",
  virtualAccountValidHours: "시간",
  minWithdrawalAmount: "P",
  withdrawalFee: "원",
  withdrawalDeadlineDays: "영업일",
};

function formatValue(value) {
  const num = Number(value);
  if (!isNaN(num) && value !== "" && value !== null) {
    return num.toLocaleString();
  }
  return value;
}

function WalletPolicy() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedKey, setSavedKey] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await fetchPolicies();
        setPolicies(data.policies);
      } catch {
        setError("정책 목록을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function handleEditClick(policy) {
    setEditingKey(policy.policyKey);
    setEditValue("");
    setError("");
    setSavedKey(null);
  }

  function handleCancel() {
    setEditingKey(null);
    setError("");
  }

  async function handleSave(policy) {
    if (!editValue.trim()) {
      setError("새 값을 입력해주세요.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await updatePolicy(policy.policyKey, editValue.trim(), "-");
      const data = await fetchPolicies();
      setPolicies(data.policies);
      setEditingKey(null);
      setSavedKey(policy.policyKey);
      setTimeout(() => setSavedKey(null), 2000);
    } catch (err) {
      setError(
        err?.response?.data?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "정책 업데이트에 실패했습니다.",
      );
    } finally {
      setSaving(false);
    }
  }

  const btnStyle = (primary) => ({
    height: "32px",
    padding: "0 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "6px",
    cursor: saving ? "not-allowed" : "pointer",
    border: "none",
    background: primary ? "#6366f1" : "#f4f4f5",
    color: primary ? "white" : "#52525b",
  });

  return (
    <Layout>
      <div className="wallet-policy">
        <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>
          정책 설정
        </h4>

        {error && (
          <div
            style={{
              padding: "10px 14px",
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: "8px",
              color: "#be123c",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}
          >
            불러오는 중...
          </div>
        ) : (
          <div className="wp-section">
            {policies.map((policy) => {
              const isEditing = editingKey === policy.policyKey;
              const isSaved = savedKey === policy.policyKey;
              const unit = UNIT_MAP[policy.policyKey] || "";

              return (
                <div
                  key={policy.policyKey}
                  className={`wp-policy-row${isSaved ? " wp-policy-row--saved" : ""}`}
                >
                  <div className="wp-policy-label">{policy.label}</div>

                  <div className="wp-policy-value-area">
                    {isEditing ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            color: "#9ca3af",
                            textDecoration: "line-through",
                          }}
                        >
                          {formatValue(policy.policyValue)}
                          {unit && ` ${unit}`}
                        </span>
                        <span style={{ color: "#d1d5db", fontSize: "13px" }}>
                          →
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleSave(policy)
                            }
                            placeholder="변경 후 값"
                            autoFocus
                            style={{
                              height: "32px",
                              width: "110px",
                              padding: "0 10px",
                              border: "1px solid #6366f1",
                              borderRadius: "6px",
                              fontSize: "14px",
                              fontWeight: 600,
                              outline: "none",
                              color: "#111827",
                            }}
                          />
                          {unit && (
                            <span
                              style={{ fontSize: "13px", color: "#6b7280" }}
                            >
                              {unit}
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span className="wp-policy-value">
                          {formatValue(policy.policyValue)}
                        </span>
                        {unit && <span className="wp-policy-unit">{unit}</span>}
                        {isSaved && (
                          <span
                            style={{
                              fontSize: "13px",
                              color: "#16a34a",
                              fontWeight: 600,
                            }}
                          >
                            ✓ 저장됨
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 수정 버튼(PUT admin/wallet/policies/:key)은 현재 주석 처리돼 렌더되지 않는다.
                      되살릴 때는 hasPermission("wallet.policy.update")일 때만 렌더한다 (R-FE-04, 비활성이 아니라 숨김). */}
                  {/* <div className="wp-policy-action">
                    {policy.editable &&
                      (isEditing ? (
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            onClick={() => handleSave(policy)}
                            disabled={saving}
                            style={btnStyle(true)}
                          >
                            {saving ? "저장 중..." : "저장"}
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={saving}
                            style={btnStyle(false)}
                          >
                            취소
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEditClick(policy)}
                          style={btnStyle(false)}
                        >
                          수정
                        </button>
                      ))}
                  </div> */}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default WalletPolicy;
